import { useState, useCallback, useEffect, useRef } from 'react';
import { Post as PostType } from '../types';
import { api } from '../services/api';
import { useAuth } from '../global-components/src';

export interface ReactionType {
    slug: string;
    emoji: string;
    label: string;
}

interface UsePostReactionOptions {
    post: PostType;
    setPosts?: React.Dispatch<React.SetStateAction<PostType[]>>;
}

export function usePostReaction({ post, setPosts }: UsePostReactionOptions) {
    const [userReaction, setUserReaction] = useState<string | null>(
        post.user_reaction ?? (post.liked ? "like" : null)
    );
    const [totalLikes, setTotalLikes] = useState<number>(post.total_likes ?? 0);
    const [isLoading, setIsLoading] = useState(false);
    const lastReqRef = useRef<AbortController | null>(null);
    const { token } = useAuth();

    useEffect(() => {
        setUserReaction(post.user_reaction ?? (post.liked ? "like" : null));
        setTotalLikes(post.total_likes ?? 0);
    }, [post.id, post.user_reaction, post.liked, post.total_likes]);

    const react = useCallback(async (slug: string) => {
        if (isLoading) return;

        const prevReaction = userReaction;
        const prevTotal    = totalLikes;

        // Calcula os estados otimísticos
        const nextReaction = userReaction === slug ? null : slug;
        const nextTotal =
            userReaction === slug ? Math.max(0, totalLikes - 1) :
            userReaction          ? totalLikes :                  // troca: total não muda
                                    totalLikes + 1;

        setUserReaction(nextReaction);
        setTotalLikes(nextTotal);

        // Update otimístico imediato — inclui reaction_summary
        setPosts?.(prev =>
            prev.map(p => {
                if (p.id !== post.id) return p;

                const currentSummary: string[] = (p as any).reaction_summary ?? [];
                const optimisticSummary = computeOptimisticSummary(
                    currentSummary,
                    prevReaction,
                    nextReaction,
                );

                return {
                    ...p,
                    total_likes:      nextTotal,
                    user_reaction:    nextReaction,
                    liked:            !!nextReaction,
                    reaction_summary: optimisticSummary,
                } as any;
            })
        );

        lastReqRef.current?.abort();
        const controller = new AbortController();
        lastReqRef.current = controller;

        setIsLoading(true);
        try {
            const { data } = await api(token).post(
                '/posts/react',
                { id_post: post.id, reaction_slug: slug },
                { signal: controller.signal }
            );

            setUserReaction(data.reaction ?? null);

            // Confirma com os dados reais do backend
            setPosts?.(prev =>
                prev.map(p => {
                    if (p.id !== post.id) return p;
                    const delta =
                        data.message === 'removed' ? -1 :
                        data.message === 'success' ? 1 :
                        0;
                    return {
                        ...p,
                        total_likes:      Math.max(0, p.total_likes + delta),
                        user_reaction:    data.reaction ?? null,
                        liked:            !!data.reaction,
                        reaction_summary: data.reaction_summary ?? p.reaction_summary, // ← backend confirma
                    } as any;
                })
            );
        } catch (err: any) {
            if (err.name === 'CanceledError') return;
            // Reverte tudo incluindo summary
            setUserReaction(prevReaction);
            setTotalLikes(prevTotal);
            setPosts?.(prev =>
                prev.map(p => {
                    if (p.id !== post.id) return p;
                    return {
                        ...p,
                        total_likes:      prevTotal,
                        user_reaction:    prevReaction,
                        liked:            !!prevReaction,
                        reaction_summary: (post as any).reaction_summary ?? [],
                    } as any;
                })
            );
        } finally {
            setIsLoading(false);
        }
    }, [isLoading, userReaction, totalLikes, post, setPosts, token]);

    function computeOptimisticSummary(
        currentSummary: string[],
        prevReaction: string | null,
        nextReaction: string | null,
    ): string[] {
        const normalizedSummary: string[] =
            typeof currentSummary === "string"
                ? (JSON.parse(currentSummary) as string[])
                : currentSummary;

        const set = new Set(normalizedSummary);

        if (nextReaction === null && prevReaction) {
            // Remoção: tira o slug (otimista — backend confirma se outros ainda votaram)
            set.delete(prevReaction);
        }
        // Troca: mantém o antigo no summary (outros usuários podem ter votado nele)
        // só garante que o novo entra

        if (nextReaction) {
            set.add(nextReaction);
        }

        // Preserva a ordem já confirmada pelo backend. Uma reação nova entra no
        // final e a resposta da API faz qualquer reordenação baseada nas contagens.
        return Array.from(set).slice(0, 3);
    }

    return { userReaction, totalLikes, react, isLoading };
}